import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  CalendarBlank,
  Trash,
  Plus,
  FloppyDisk,
  Scales,
  Barn,
  Truck,
  PencilSimple,
  NotePencil,
  Table,
  CurrencyDollar,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, ANIMAL_TYPES, computeTotals, partialTotals, formatBRL, formatNum } from "@/lib/gado";
import { ScaleEntry } from "@/components/ScaleMode";

const num = (v) => (v === "" || v == null ? null : parseFloat(String(v).replace(",", ".")));

function Field({ label, children, compact = false }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.12em] text-gado-muted font-semibold">{label}</Label>
      <div className={compact ? "mt-1" : "mt-1"}>{children}</div>
    </div>
  );
}

function GroupHeading({ children, right = null }) {
  return (
    <div className="flex items-center justify-between mb-2.5">
      <div className="flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-gado-gold" />
        <span className="text-xs uppercase tracking-[0.2em] text-gado-muted font-bold">{children}</span>
      </div>
      {right}
    </div>
  );
}

export default function NovaPesagem() {
  const [params] = useSearchParams();
  const editId = params.get("id");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [transportOpen, setTransportOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [weighMode, setWeighMode] = useState("balanca"); // balanca (principal) | planilha

  const { data: buyers = [] } = useQuery({ queryKey: ["buyers"], queryFn: async () => (await api.get("/buyers")).data });
  const { data: partners = [] } = useQuery({ queryKey: ["partners"], queryFn: async () => (await api.get("/partners")).data });
  const { data: farms = [] } = useQuery({ queryKey: ["farms"], queryFn: async () => (await api.get("/farms")).data });

  const [f, setF] = useState({
    date: new Date(),
    animal_type: "Boi",
    iron_brand: "",
    buyer_name: "",
    partner_name: "",
    city_district: "",
    transport_driver_name: "",
    transport_plate: "",
    transport_phone: "",
    transport_km_start: "",
    transport_km_end: "",
    transport_km_value: "",
    transport_city_start: "",
    transport_city_end: "",
    transport_freight_value: "",
    price_per_arroba: "",
    pricing_mode: "arroba", // "arroba" | "kg" — define o significado do valor digitado
    discount_kg: "",
    bank_data: "",
    observations: "",
  });
  const [animals, setAnimals] = useState([{ weight: "", quantity: "" }]);
  const [partials, setPartials] = useState([]);

  const set = (k) => (e) =>
    setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  // Valor efetivo por arroba: no modo "kg", o valor digitado é R$/kg e convertido (× 30).
  // Como total = arrobas × R$/@ = (peso/30) × (R$/kg × 30) = peso × R$/kg, o resultado é idêntico.
  const effectivePriceArroba = useMemo(() => {
    const v = num(f.price_per_arroba) || 0;
    return f.pricing_mode === "kg" ? v * 30 : v;
  }, [f.price_per_arroba, f.pricing_mode]);

  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const { data } = await api.get(`/transactions/${editId}`);
        setF({
          date: data.date ? new Date(data.date) : new Date(),
          animal_type: data.animal_type || "Boi",
          iron_brand: data.iron_brand || "",
          buyer_name: data.buyer_name || "",
          partner_name: data.partner_name || "",
          city_district: data.city_district || "",
          transport_driver_name: data.transport_driver_name || "",
          transport_plate: data.transport_plate || "",
          transport_phone: data.transport_phone || "",
          transport_km_start: data.transport_km_start ?? "",
          transport_km_end: data.transport_km_end ?? "",
          transport_km_value: data.transport_km_value ?? "",
          transport_city_start: data.transport_city_start || "",
          transport_city_end: data.transport_city_end || "",
          transport_freight_value: data.transport_freight_value ?? "",
          price_per_arroba:
            data.pricing_mode === "kg"
              ? (data.price_per_kg ?? "")
              : (data.price_per_arroba ?? ""),
          pricing_mode: data.pricing_mode || "arroba",
          discount_kg: data.partial_weighings?.length ? "" : (data.discount_kg ?? ""),
          bank_data: data.bank_data || "",
          observations: data.observations || "",
        });
        if (data.animals?.length)
          setAnimals(
            data.animals.map((a) => ({
              weight: a.weight ?? "",
              quantity: a.quantity ?? "",
            }))
          );
        if (data.partial_weighings?.length)
          setPartials(
            data.partial_weighings.map((pw) => ({
              farmer_name: pw.farmer_name || "",
              animal_type: pw.animal_type || "Boi",
              iron_brand: pw.iron_brand || "",
              buyer_name: pw.buyer_name || data.buyer_name || "",
              partner_name: pw.partner_name || "",
              city_district: pw.city_district || "",
              bank_data: pw.bank_data || "",
              observations: pw.observations || "",
              price_per_arroba: pw.price_per_arroba ?? data.price_per_arroba ?? 0,
              discount_kg: pw.discount_kg ?? 0,
              animals: (pw.animals || []).map((a) => ({
                number: a.number || 0,
                weight: Number(a.weight) || 0,
                quantity: Number(a.quantity) || 0,
              })),
              total_quantity: pw.total_quantity || 0,
              total_weight: pw.total_weight || 0,
              total_arrobas: pw.total_arrobas || 0,
              avg_weight: pw.avg_weight || 0,
              avg_arrobas: pw.avg_arrobas || 0,
            }))
          );
      } catch {
        toast.error("Não foi possível carregar a pesagem");
      }
    })();
  }, [editId]);

  // ---- Auto-save draft (localStorage, only for new pesagens — never for /edit) ----
  const DRAFT_KEY = "gadopro:draft:nova-pesagem";
  const [draftRestored, setDraftRestored] = useState(false);

  // Restore draft on mount (only when NOT editing an existing tx)
  useEffect(() => {
    if (editId) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      // Only restore drafts newer than 7 days
      if (draft?.savedAt && Date.now() - draft.savedAt > 7 * 24 * 3600 * 1000) {
        localStorage.removeItem(DRAFT_KEY);
        return;
      }
      // Only restore if there's actually meaningful content
      const hasContent =
        draft.f?.buyer_name || draft.f?.price_per_arroba ||
        (draft.animals || []).some((a) => a.weight || a.quantity) ||
        (draft.partials || []).length > 0;
      if (!hasContent) {
        localStorage.removeItem(DRAFT_KEY);
        return;
      }
      if (draft.f) {
        setF((prev) => ({
          ...prev,
          ...draft.f,
          date: draft.f.date ? new Date(draft.f.date) : prev.date,
        }));
      }
      if (draft.animals?.length) setAnimals(draft.animals);
      if (draft.partials?.length) setPartials(draft.partials);
      setDraftRestored(true);
      toast.success("Rascunho restaurado", {
        description: "Continuamos de onde você parou.",
        action: {
          label: "Descartar",
          onClick: () => {
            localStorage.removeItem(DRAFT_KEY);
            window.location.reload();
          },
        },
      });
    } catch {
      /* ignore corrupted draft */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist draft on every change (debounced)
  useEffect(() => {
    if (editId) return; // don't overwrite when editing
    const t = setTimeout(() => {
      const hasContent =
        f.buyer_name || f.price_per_arroba ||
        animals.some((a) => a.weight || a.quantity) ||
        partials.length > 0;
      if (!hasContent) {
        localStorage.removeItem(DRAFT_KEY);
        return;
      }
      try {
        localStorage.setItem(
          DRAFT_KEY,
          JSON.stringify({ savedAt: Date.now(), f, animals, partials }),
        );
      } catch {
        /* storage full — ignore */
      }
    }, 500);
    return () => clearTimeout(t);
  }, [editId, f, animals, partials]);

  // Auto-append a new empty row when the last row has both weight & quantity filled.
  const autoAppend = (list) => {
    const last = list[list.length - 1];
    if (!last) return list;
    const w = num(last.weight);
    const q = num(last.quantity);
    if (w && w > 0 && q && q > 0) {
      return [...list, { weight: "", quantity: "" }];
    }
    return list;
  };

  const setAnimal = (i, k, v) =>
    setAnimals((p) => autoAppend(p.map((a, idx) => (idx === i ? { ...a, [k]: v } : a))));
  const removeAnimal = (i) =>
    setAnimals((p) => (p.length === 1 ? p : p.filter((_, idx) => idx !== i)));

  // ---- Partial weighings (original flow: save current form as a partial, clear, next one) ----
  const [partialModalOpen, setPartialModalOpen] = useState(false);
  const [pendingFarmerName, setPendingFarmerName] = useState("");
  const [editingPartialIndex, setEditingPartialIndex] = useState(null);

  const clearFormForNextPartial = () => {
    setAnimals([{ weight: "", quantity: "" }]);
    setF((p) => ({
      ...p,
      // KEEP: date, transport fields (opt-in via modal)
      // CLEAR everything else typable — inclui Valor e Desconto (cada parcial tem seu valor)
      animal_type: "Boi",
      iron_brand: "",
      buyer_name: "",
      partner_name: "",
      city_district: "",
      bank_data: "",
      observations: "",
      price_per_arroba: "",
      discount_kg: "",
    }));
  };

  const openPartialModal = () => {
    if (!num(f.price_per_arroba)) {
      toast.error(f.pricing_mode === "kg" ? "Informe o valor por quilo" : "Informe o valor por arroba");
      return;
    }
    if (!f.buyer_name?.trim()) {
      toast.error("Informe o comprador");
      return;
    }
    if (!f.partner_name?.trim()) {
      toast.error("Informe o parceiro");
      return;
    }
    const valid = parsedAnimals.filter((a) => a.weight > 0 && a.quantity > 0);
    if (valid.length === 0) {
      toast.error("Adicione ao menos um animal com peso");
      return;
    }
    setPendingFarmerName("");
    setPartialModalOpen(true);
  };

  const confirmSavePartial = () => {
    const valid = parsedAnimals.filter((a) => a.weight > 0 && a.quantity > 0);
    const discount = num(f.discount_kg) || 0;
    const pt = partialTotals(valid, discount);
    const partial = {
      farmer_name: pendingFarmerName.trim() || null,
      animal_type: f.animal_type,
      iron_brand: f.iron_brand || null,
      buyer_name: f.buyer_name || null,
      partner_name: f.partner_name || null,
      city_district: f.city_district || null,
      bank_data: f.bank_data || null,
      observations: f.observations || null,
      price_per_arroba: effectivePriceArroba,
      discount_kg: discount,
      animals: valid,
      ...pt,
    };
    setPartials((prev) => {
      if (editingPartialIndex != null) {
        const next = [...prev];
        next[editingPartialIndex] = partial;
        return next;
      }
      return [...prev, partial];
    });
    setEditingPartialIndex(null);
    setPartialModalOpen(false);
    clearFormForNextPartial();
    const label = partial.farmer_name || `Pesagem ${partials.length + 1}`;
    toast.success(`Parcial salva: ${label} — ${pt.total_quantity} cab · ${pt.total_weight.toFixed(0)} kg`);
  };

  const editPartial = (i) => {
    const pw = partials[i];
    setF((p) => ({
      ...p,
      animal_type: pw.animal_type || p.animal_type,
      iron_brand: pw.iron_brand || "",
      buyer_name: pw.buyer_name || p.buyer_name,
      partner_name: pw.partner_name || "",
      city_district: pw.city_district || "",
      bank_data: pw.bank_data || "",
      observations: pw.observations || "",
      price_per_arroba: pw.price_per_arroba ?? p.price_per_arroba,
      discount_kg: pw.discount_kg ?? "",
    }));
    const rows = pw.animals?.length
      ? pw.animals.map((a) => ({ weight: String(a.weight ?? ""), quantity: String(a.quantity ?? "") }))
      : [{ weight: "", quantity: "" }];
    setAnimals([...rows, { weight: "", quantity: "" }]);
    setEditingPartialIndex(i);
    setPendingFarmerName(pw.farmer_name || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Editando pesagem parcial. Ao salvar, ela substitui a linha anterior.");
  };

  const removePartial = (i) => {
    setPartials((p) => p.filter((_, idx) => idx !== i));
    if (editingPartialIndex === i) setEditingPartialIndex(null);
  };

  const parsedAnimals = useMemo(
    () =>
      animals.map((a, i) => ({
        number: i + 1,
        weight: num(a.weight) || 0,
        quantity: Number(String(a.quantity).replace(",", ".")) || 0,
      })),
    [animals]
  );

  // For live totals: pass saved partials + current-form animals into computeTotals
  const parsedPartials = useMemo(
    () =>
      partials.map((pw) => ({
        animals: pw.animals || [],
        discount_kg: Number(pw.discount_kg) || 0,
      })),
    [partials]
  );

  const totals = useMemo(
    () =>
      computeTotals({
        animals: parsedAnimals,
        price_per_arroba: effectivePriceArroba,
        discount_kg: num(f.discount_kg) || 0,
        partial_weighings: parsedPartials,
      }),
    [parsedAnimals, parsedPartials, effectivePriceArroba, f.discount_kg]
  );

  // Current-form-only totals (no partials) — shown as "Pesagem atual"
  const currentTotals = useMemo(
    () =>
      computeTotals({
        animals: parsedAnimals,
        price_per_arroba: effectivePriceArroba,
        discount_kg: num(f.discount_kg) || 0,
      }),
    [parsedAnimals, effectivePriceArroba, f.discount_kg]
  );

  // Frete ao vivo: (KM final − KM inicial) × 2 (ida e volta) × R$/km
  const computedFreightKm = Math.max((num(f.transport_km_end) || 0) - (num(f.transport_km_start) || 0), 0);
  const computedFreight = computedFreightKm * 2 * (num(f.transport_km_value) || 0);

  const submit = async () => {
    if (!num(f.price_per_arroba))
      return toast.error(f.pricing_mode === "kg" ? "Informe o valor por quilo" : "Informe o valor por arroba");
    if (!f.buyer_name?.trim()) return toast.error("Informe o comprador");
    if (!f.partner_name?.trim()) return toast.error("Informe o parceiro");
    if (totals.total_quantity === 0) return toast.error("Adicione ao menos um animal com peso");
    setSaving(true);

    // If there are saved partials AND current form still has animals, auto-add current as last partial.
    const cleanCurrent = parsedAnimals.filter((a) => a.weight > 0 && a.quantity > 0);
    let allPartials = [...partials];
    let mainAnimals = cleanCurrent;
    if (allPartials.length > 0 && cleanCurrent.length > 0) {
      const disc = num(f.discount_kg) || 0;
      const pt = partialTotals(cleanCurrent, disc);
      allPartials.push({
        farmer_name: null,
        animal_type: f.animal_type,
        iron_brand: f.iron_brand || null,
        buyer_name: f.buyer_name || null,
        partner_name: f.partner_name || null,
        city_district: f.city_district || null,
        bank_data: f.bank_data || null,
        observations: f.observations || null,
        price_per_arroba: effectivePriceArroba,
        discount_kg: disc,
        animals: cleanCurrent,
        ...pt,
      });
      mainAnimals = [];
    }

    const partial_weighings = allPartials.length
      ? allPartials.map((pw) => ({
          farmer_name: pw.farmer_name || null,
          date: null,
          animal_type: pw.animal_type || null,
          iron_brand: pw.iron_brand || null,
          buyer_name: pw.buyer_name || null,
          partner_name: pw.partner_name || null,
          city_district: pw.city_district || null,
          bank_data: pw.bank_data || null,
          observations: pw.observations || null,
          price_per_arroba: effectivePriceArroba,
          discount_kg: Number(pw.discount_kg) || 0,
          animals: pw.animals,
          total_quantity: pw.total_quantity,
          total_weight: pw.total_weight,
          total_arrobas: pw.total_arrobas,
          avg_weight: pw.avg_weight,
          avg_arrobas: pw.avg_arrobas,
        }))
      : null;

    const payload = {
      date: f.date.toISOString(),
      animal_type: f.animal_type,
      iron_brand: f.iron_brand || null,
      buyer_name: f.buyer_name || null,
      partner_name: f.partner_name || null,
      city_district: f.city_district || null,
      transport_driver_name: f.transport_driver_name || null,
      transport_plate: f.transport_plate || null,
      transport_phone: f.transport_phone || null,
      transport_km_start: num(f.transport_km_start),
      transport_km_end: num(f.transport_km_end),
      transport_km_value: num(f.transport_km_value),
      transport_city_start: f.transport_city_start || null,
      transport_city_end: f.transport_city_end || null,
      transport_freight_value: computedFreight > 0 ? computedFreight : (num(f.transport_freight_value) || null),
      animals: mainAnimals,
      partial_weighings,
      price_per_arroba: effectivePriceArroba,
      pricing_mode: f.pricing_mode,
      price_per_kg:
        f.pricing_mode === "kg"
          ? num(f.price_per_arroba)
          : num(f.price_per_arroba) != null
          ? num(f.price_per_arroba) / 30
          : null,
      discount_kg: allPartials.length > 0 ? null : num(f.discount_kg),
      bank_data: f.bank_data || null,
      observations: f.observations || null,
      status: "finalizada",
    };
    try {
      if (editId) await api.put(`/transactions/${editId}`, payload);
      else await api.post("/transactions", payload);
      qc.invalidateQueries({ queryKey: ["transactions"] });
      // Draft consumed — clear it so the next Nova Pesagem starts blank
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      toast.success(editId ? "Pesagem atualizada" : "Pesagem registrada");
      navigate("/app/historico");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="nova-pesagem-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">
            {editId ? "Editar Pesagem" : "Nova Pesagem"}
          </h1>
          <p className="text-gado-muted text-sm">Preencha os dados da compra e acompanhe o total ao vivo.</p>
        </div>
        {draftRestored && !editId && (
          <span
            data-testid="draft-restored-pill"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-gado-gold/15 border border-gado-gold/30 text-gado-gold text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 self-center"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-gado-gold animate-pulse" />
            Rascunho ativo
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left — dense unified form */}
        <div className="lg:col-span-8 space-y-4">
          <section className="rounded-2xl border border-gado-line bg-white p-4 md:p-5" data-testid="nova-pesagem-form">
            {/* --- Dados da pesagem --- */}
            <div>
              <GroupHeading>Dados Pesagem</GroupHeading>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Data">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="date-trigger"
                        className="w-full justify-start font-normal h-9 px-2.5 text-xs md:text-sm"
                      >
                        <CalendarBlank size={14} className="mr-1.5 text-gado-muted shrink-0" />
                        <span className="truncate">{format(f.date, "dd/MM/yyyy")}</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={f.date}
                        onSelect={(d) => d && set("date")(d)}
                        locale={ptBR}
                      />
                    </PopoverContent>
                  </Popover>
                </Field>

                <Field label="Tipo">
                  <Select value={f.animal_type} onValueChange={set("animal_type")}>
                    <SelectTrigger data-testid="animal-type-select" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ANIMAL_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Ferro">
                  <Input data-testid="iron-input" value={f.iron_brand} onChange={set("iron_brand")} placeholder="JB" autoComplete="off" autoCorrect="off" spellCheck={false} name="iron-brand" className="h-9 placeholder:text-[11px]" />
                </Field>

                <Field label="Cidade">
                  <Input data-testid="city-input" value={f.city_district} onChange={set("city_district")} placeholder="Cidade" autoComplete="off" autoCorrect="off" spellCheck={false} name="city-district" className="h-9 placeholder:text-[11px]" />
                </Field>

                <Field label="Comprador *">
                  <Input
                    data-testid="buyer-input"
                    list="buyers-list"
                    value={f.buyer_name}
                    onChange={set("buyer_name")}
                    placeholder="Nome do comprador"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    name="buyer-name"
                    className="h-9 placeholder:text-[11px]"
                  />
                  <datalist id="buyers-list">
                    {buyers.map((b) => <option key={b.buyer_id} value={b.name} />)}
                  </datalist>
                </Field>

                <Field label="Parceiro *">
                  <Input
                    data-testid="partner-input"
                    list="partners-list"
                    value={f.partner_name}
                    onChange={set("partner_name")}
                    placeholder="Nome do parceiro"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    name="partner-name"
                    className="h-9 placeholder:text-[11px]"
                  />
                  <datalist id="partners-list">
                    {partners.map((p) => <option key={p.partner_id} value={p.name} />)}
                  </datalist>
                </Field>
              </div>
            </div>

          </section>

          {/* --- Pesagem do animal — card em destaque --- */}
          <section
            className="rounded-2xl border-2 border-gado-green/50 bg-gradient-to-b from-gado-green/[0.05] to-white p-4 md:p-5 space-y-5 shadow-lg shadow-gado-green/10"
            data-testid="animal-weighing-card"
          >
            <div>
              <GroupHeading
                right={
                  <div className="inline-flex rounded-lg bg-gado-line/40 p-0.5" data-testid="weigh-mode-toggle">
                    <button
                      type="button"
                      onClick={() => setWeighMode("balanca")}
                      data-testid="weigh-mode-balanca"
                      aria-pressed={weighMode === "balanca"}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${
                        weighMode === "balanca"
                          ? "bg-gado-green text-white shadow-sm"
                          : "text-gado-muted hover:text-gado-ink"
                      }`}
                    >
                      <Scales size={12} weight="fill" /> Balança
                    </button>
                    <button
                      type="button"
                      onClick={() => setWeighMode("planilha")}
                      data-testid="weigh-mode-planilha"
                      aria-pressed={weighMode === "planilha"}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${
                        weighMode === "planilha"
                          ? "bg-gado-green text-white shadow-sm"
                          : "text-gado-muted hover:text-gado-ink"
                      }`}
                    >
                      <Table size={12} /> Planilha
                    </button>
                  </div>
                }
              >
                <span className="inline-flex items-center gap-1.5 text-gado-green">
                  <Scales size={13} weight="fill" />
                  <span className="text-xs">Pesagem do Animal</span>
                </span>
              </GroupHeading>
              {weighMode === "balanca" ? (
                <ScaleEntry
                  animals={animals}
                  setAnimals={setAnimals}
                  price_per_arroba={effectivePriceArroba}
                  discount_kg={f.discount_kg}
                />
              ) : (
              <div className="space-y-2" data-testid="planilha-mode">
                <div className="grid grid-cols-12 gap-2 text-xs uppercase tracking-wider text-gado-muted font-semibold px-1">
                  <span className="col-span-6">Peso (kg)</span>
                  <span className="col-span-5">Quantidade</span>
                  <span className="col-span-1" />
                </div>
                {animals.map((a, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`animal-row-${i}`}>
                    <Input
                      className="col-span-6 no-spin h-9 font-mono"
                      type="text"
                      inputMode="decimal"
                      enterKeyHint="next"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      name={`animal-weight-${i}`}
                      placeholder="0,00"
                      value={a.weight}
                      onChange={(e) => setAnimal(i, "weight", e.target.value.replace(/[^0-9.,]/g, ""))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const nextInput = document.querySelector(`[data-testid="animal-qty-${i}"]`);
                          if (nextInput) nextInput.focus();
                        }
                      }}
                      data-testid={`animal-weight-${i}`}
                    />
                    <Input
                      className="col-span-5 no-spin h-9 font-mono"
                      type="text"
                      inputMode="numeric"
                      enterKeyHint="next"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      name={`animal-qty-${i}`}
                      placeholder="0"
                      value={a.quantity}
                      onChange={(e) => setAnimal(i, "quantity", e.target.value.replace(/[^0-9]/g, ""))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const nextInput = document.querySelector(`[data-testid="animal-weight-${i + 1}"]`);
                          if (nextInput) nextInput.focus();
                        }
                      }}
                      data-testid={`animal-qty-${i}`}
                    />
                    <button
                      tabIndex={-1}
                      className="col-span-1 text-gado-muted hover:text-destructive transition-colors grid place-items-center disabled:opacity-30 h-9"
                      onClick={() => removeAnimal(i)}
                      disabled={animals.length === 1}
                      data-testid={`remove-animal-${i}`}
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                ))}
              </div>
              )}
            </div>

            {/* --- Parciais salvas (só quando existem) — destaque visual --- */}
            {partials.length > 0 && (
              <div className="pt-4 border-t border-gado-green/20" data-testid="saved-partials">
                <GroupHeading>
                  <span className="inline-flex items-center gap-1">
                    <Barn size={12} className="text-gado-green" /> Parciais salvas · {partials.length}
                  </span>
                </GroupHeading>
                <div className="space-y-2">
                  {partials.map((pw, pi) => (
                    <div
                      key={pi}
                      className="flex items-center justify-between gap-2.5 rounded-xl border border-gado-gold/40 border-l-4 border-l-gado-gold bg-gado-gold/5 px-3 py-2.5 shadow-sm"
                      data-testid={`saved-partial-${pi}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-gado-ink truncate">
                          {pw.farmer_name || `Pesagem ${pi + 1}`}
                        </p>
                        <p className="text-xs text-gado-muted truncate">
                          {pw.animal_type} · {formatNum(pw.total_quantity, 0)} cab · {formatNum(pw.total_arrobas, 2)} @
                        </p>
                      </div>
                      <span className="shrink-0 font-mono text-xs sm:text-sm font-bold text-gado-gold tabular-nums bg-gado-gold/10 border border-gado-gold/25 rounded-lg px-2 py-1">
                        {formatBRL(pw.total_arrobas * (pw.price_per_arroba || 0))}
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => editPartial(pi)} data-testid={`edit-partial-${pi}`} className="p-1.5 rounded-md text-gado-muted hover:bg-gado-line/50 hover:text-gado-ink">
                          <PencilSimple size={14} />
                        </button>
                        <button onClick={() => removePartial(pi)} data-testid={`remove-partial-${pi}`} className="p-1.5 rounded-md text-gado-muted hover:bg-destructive/10 hover:text-destructive">
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* --- Valores da Pesagem --- */}
          <section className="rounded-2xl border border-gado-line bg-white p-4 md:p-5" data-testid="pricing-section">
            <GroupHeading>
              <span className="inline-flex items-center gap-1.5 text-gado-green">
                <CurrencyDollar size={13} weight="fill" />
                <span className="text-xs">Valores da Pesagem</span>
              </span>
            </GroupHeading>

            {/* Modo de cálculo: por arroba ou por quilo */}
            <div className="inline-flex rounded-lg bg-gado-line/40 p-0.5 mb-3" data-testid="pricing-mode-toggle">
              <button
                type="button"
                onClick={() => set("pricing_mode")("arroba")}
                data-testid="pricing-mode-arroba"
                aria-pressed={f.pricing_mode === "arroba"}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                  f.pricing_mode === "arroba"
                    ? "bg-gado-green text-white shadow-sm"
                    : "text-gado-muted hover:text-gado-ink"
                }`}
              >
                Por Arroba (@)
              </button>
              <button
                type="button"
                onClick={() => set("pricing_mode")("kg")}
                data-testid="pricing-mode-kg"
                aria-pressed={f.pricing_mode === "kg"}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                  f.pricing_mode === "kg"
                    ? "bg-gado-green text-white shadow-sm"
                    : "text-gado-muted hover:text-gado-ink"
                }`}
              >
                Por Quilo (kg)
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label={f.pricing_mode === "kg" ? "Valor por kg (R$) *" : "Valor por @ (R$) *"}>
                <Input
                  data-testid="price-arroba-input"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  name="price-per-arroba"
                  placeholder="0,00"
                  value={f.price_per_arroba}
                  onChange={(e) => set("price_per_arroba")({ target: { value: e.target.value.replace(/[^0-9.,]/g, "") } })}
                  className="no-spin h-9 font-mono"
                />
                {num(f.price_per_arroba) > 0 && (
                  <p className="mt-1 text-[11px] text-gado-muted tabular-nums" data-testid="price-conversion-hint">
                    ≈ {formatBRL(f.pricing_mode === "kg" ? num(f.price_per_arroba) * 30 : num(f.price_per_arroba) / 30)}{" "}
                    {f.pricing_mode === "kg" ? "por @" : "por kg"}
                  </p>
                )}
              </Field>

              <Field label="Desconto (kg)">
                <Input
                  data-testid="discount-input"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  name="discount-kg"
                  placeholder="0"
                  value={f.discount_kg}
                  onChange={(e) => set("discount_kg")({ target: { value: e.target.value.replace(/[^0-9.,]/g, "") } })}
                  className="no-spin h-9 font-mono"
                />
              </Field>
            </div>
          </section>
        </div>

        {/* Right — sticky totals */}
        <div className="lg:col-span-4">
          <div className="sticky top-4 rounded-2xl border border-gado-line bg-gado-green text-white p-4 md:p-5 space-y-4">
            <div className="flex items-center gap-2 text-gado-goldLight">
              <Scales size={16} weight="fill" />
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold">Pesagem Atual</span>
            </div>

            <div className="space-y-1.5">
              <Row label="Cabeças" value={`${formatNum(currentTotals.total_quantity, 0)}`} />
              <Row label="Peso bruto" value={`${formatNum(currentTotals.total_weight, 1)} kg`} />
              <Row label="Arrobas" value={`${formatNum(currentTotals.total_arrobas, 2)} @`} />
              <Row label="Média/cab" value={`${formatNum(currentTotals.avg_weight, 1)} kg`} />
              <Row label="Média @" value={`${formatNum(currentTotals.avg_arrobas, 2)} @`} />
              <Row
                label={f.pricing_mode === "kg" ? "Valor/kg" : "Valor/@"}
                value={formatBRL(f.pricing_mode === "kg" ? (num(f.price_per_arroba) || 0) : effectivePriceArroba)}
              />
              <Row label="Valor/animal" value={formatBRL(currentTotals.unit_price_per_animal)} />
            </div>

            <div className="border-t border-white/15 pt-3">
              <p className="text-[10px] uppercase tracking-widest text-white/60 mb-0.5">
                Valor a pagar {partials.length > 0 && "(atual)"}
              </p>
              <motion.p
                key={currentTotals.total_amount}
                initial={{ scale: 0.94, opacity: 0.6, filter: "blur(2px)" }}
                animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="font-cabinet text-2xl font-extrabold text-gado-goldLight tabular-nums"
                data-testid="current-total"
              >
                {formatBRL(currentTotals.total_amount)}
              </motion.p>
            </div>

            {partials.length > 0 && (
              <div className="rounded-lg bg-white/8 border border-white/10 p-3 space-y-1.5" data-testid="grand-totals">
                <div className="flex items-center gap-2 text-gado-goldLight mb-1">
                  <span className="text-[9px] uppercase tracking-[0.22em] font-bold">
                    Resumo Geral · {partials.length + (currentTotals.total_quantity > 0 ? 1 : 0)} pesagens
                  </span>
                </div>
                <Row label="Total animais" value={`${formatNum(totals.total_quantity, 0)}`} />
                <Row label="Peso bruto" value={`${formatNum(totals.total_weight, 1)} kg`} />
                <Row label="Arrobas" value={`${formatNum(totals.total_arrobas, 2)} @`} />
                <div className="pt-2 mt-1.5 border-t border-white/10">
                  <p className="text-[10px] uppercase tracking-widest text-white/60 mb-0.5">Total a pagar</p>
                  <motion.p
                    key={totals.total_amount}
                    initial={{ scale: 0.94, opacity: 0.6, filter: "blur(2px)" }}
                    animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="font-cabinet text-2xl font-extrabold text-gado-goldLight tabular-nums"
                    data-testid="total-amount"
                  >
                    {formatBRL(totals.total_amount)}
                  </motion.p>
                </div>
              </div>
            )}

            {/* Compact action bar: notes + transport + partial + finalize */}
            <div className="pt-2 space-y-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setNotesOpen(true)}
                  data-testid="open-notes-button"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-md border border-white/25 bg-transparent text-white/85 hover:bg-white/10 text-xs font-medium"
                >
                  <NotePencil size={14} className="text-gado-goldLight" />
                  Observações
                  {(f.bank_data || f.observations) && (
                    <span className="h-1.5 w-1.5 rounded-full bg-gado-goldLight" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setTransportOpen(true)}
                  data-testid="open-transport-button"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-md border border-white/25 bg-transparent text-white/85 hover:bg-white/10 text-xs font-medium"
                >
                  <Truck size={14} />
                  Transporte
                  {(f.transport_driver_name || f.transport_plate || computedFreight > 0) && (
                    <span className="h-1.5 w-1.5 rounded-full bg-gado-goldLight" />
                  )}
                </button>
              </div>

              <Button
                variant="outline"
                onClick={openPartialModal}
                data-testid="save-partial-button"
                className="w-full border-white/25 bg-transparent text-white hover:bg-white/10 h-10 text-sm font-medium"
              >
                <Barn size={16} className="mr-2 text-gado-goldLight" />
                {editingPartialIndex != null ? "Atualizar Parcial" : "Salvar Pesagem Parcial"}
              </Button>

              <Button
                onClick={submit}
                disabled={saving}
                data-testid="save-pesagem-button"
                className="w-full bg-gado-gold hover:bg-gado-goldLight text-gado-ink font-semibold h-11 text-sm"
              >
                <FloppyDisk size={18} className="mr-2" />
                {saving ? "Finalizando…" : editId ? "Atualizar" : "Finalizar Pesagem"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Save-partial modal (asks farmer name) */}
      <Dialog open={partialModalOpen} onOpenChange={(o) => { if (!o) setPartialModalOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="partial-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gado-ink">
              <Barn size={20} className="text-gado-green" />
              {editingPartialIndex != null ? "Atualizar Pesagem Parcial" : "Nova Pesagem Parcial"}
            </DialogTitle>
            <DialogDescription className="sr-only">Informe o nome do fazendeiro para essa pesagem parcial.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs uppercase tracking-wider text-gado-muted">Fazendeiro / vendedor</Label>
            <Input
              autoFocus
              className="mt-1.5 placeholder:text-[11px]"
              placeholder="Ex: João da Silva"
              value={pendingFarmerName}
              onChange={(e) => setPendingFarmerName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") confirmSavePartial(); }}
              data-testid="partial-farmer-input"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              name="farmer-name"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartialModalOpen(false)}>Cancelar</Button>
            <Button onClick={confirmSavePartial} data-testid="partial-confirm-button" className="bg-gado-green hover:bg-gado-greenHover text-white">
              {editingPartialIndex != null ? "Atualizar Parcial" : "Salvar Parcial"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notes / bank data — modal */}
      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="max-w-xl" data-testid="notes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gado-ink">
              <NotePencil size={20} className="text-gado-green" /> Observações da pesagem
            </DialogTitle>
            <DialogDescription className="sr-only">Formulário para dados bancários e observações da pesagem.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2">
            <Field label="Dados bancários">
              <Textarea
                data-testid="bank-data-input"
                value={f.bank_data}
                onChange={set("bank_data")}
                placeholder="Banco, agência, conta, PIX…"
                rows={3}
                className="placeholder:text-[11px]"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                name="bank-data"
              />
            </Field>
            <Field label="Observações">
              <Textarea
                data-testid="observations-input"
                value={f.observations}
                onChange={set("observations")}
                placeholder="Observações da negociação"
                rows={4}
                className="placeholder:text-[11px]"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                name="observations"
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesOpen(false)}>Cancelar</Button>
            <Button onClick={() => setNotesOpen(false)} data-testid="notes-save" className="bg-gado-green hover:bg-gado-greenHover text-white">
              Salvar observações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transport data — modal */}
      <Dialog open={transportOpen} onOpenChange={setTransportOpen}>
        <DialogContent className="max-w-2xl" data-testid="transport-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gado-ink">
              <Truck size={20} className="text-gado-green" /> Dados do Transporte
            </DialogTitle>
            <DialogDescription className="sr-only">Formulário de dados do motorista, veículo e cálculo de frete.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <Field label="Motorista">
              <Input value={f.transport_driver_name} onChange={set("transport_driver_name")} placeholder="Nome" data-testid="tr-driver" autoComplete="off" autoCorrect="off" spellCheck={false} name="tr-driver" className="placeholder:text-[11px]" />
            </Field>
            <Field label="Placa">
              <Input value={f.transport_plate} onChange={set("transport_plate")} placeholder="ABC-1234" data-testid="tr-plate" autoComplete="off" autoCorrect="off" spellCheck={false} name="tr-plate" className="placeholder:text-[11px]" />
            </Field>
            <Field label="Telefone">
              <Input value={f.transport_phone} onChange={set("transport_phone")} placeholder="(00) 00000-0000" data-testid="tr-phone" autoComplete="off" autoCorrect="off" spellCheck={false} name="tr-phone" inputMode="tel" className="placeholder:text-[11px]" />
            </Field>
          </div>

          {/* Subdivisões do frete: início e fim */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-lg border border-gado-line/70 bg-gado-bg/40 p-3" data-testid="tr-freight-start">
              <p className="text-[10px] uppercase tracking-[0.16em] text-gado-green font-bold mb-2">Início do frete</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="KM início">
                  <Input type="text" inputMode="numeric" autoComplete="off" autoCorrect="off" spellCheck={false} name="tr-km-start" className="no-spin" value={f.transport_km_start} onChange={(e) => set("transport_km_start")({ target: { value: e.target.value.replace(/[^0-9]/g, "") } })} placeholder="0" data-testid="tr-km-start" />
                </Field>
                <Field label="Cidade">
                  <Input value={f.transport_city_start} onChange={set("transport_city_start")} placeholder="Origem" data-testid="tr-city-start" autoComplete="off" autoCorrect="off" spellCheck={false} name="tr-city-start" className="placeholder:text-[11px]" />
                </Field>
              </div>
            </div>
            <div className="rounded-lg border border-gado-line/70 bg-gado-bg/40 p-3" data-testid="tr-freight-end">
              <p className="text-[10px] uppercase tracking-[0.16em] text-gado-green font-bold mb-2">Final do frete</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="KM final">
                  <Input type="text" inputMode="numeric" autoComplete="off" autoCorrect="off" spellCheck={false} name="tr-km-end" className="no-spin" value={f.transport_km_end} onChange={(e) => set("transport_km_end")({ target: { value: e.target.value.replace(/[^0-9]/g, "") } })} placeholder="0" data-testid="tr-km-end" />
                </Field>
                <Field label="Cidade">
                  <Input value={f.transport_city_end} onChange={set("transport_city_end")} placeholder="Destino" data-testid="tr-city-end" autoComplete="off" autoCorrect="off" spellCheck={false} name="tr-city-end" className="placeholder:text-[11px]" />
                </Field>
              </div>
            </div>
          </div>

          <div className="mt-3">
            <Field label="Valor do KM (R$)">
              <Input type="text" inputMode="decimal" autoComplete="off" autoCorrect="off" spellCheck={false} name="tr-km-value" className="no-spin" value={f.transport_km_value} onChange={(e) => set("transport_km_value")({ target: { value: e.target.value.replace(/[^0-9.,]/g, "") } })} placeholder="0,00" data-testid="tr-km-value" />
            </Field>
          </div>

          {/* Live-calculated freight */}
          <div
            className="mt-1 mb-1 rounded-lg border border-gado-line bg-gado-green/5 px-4 py-3 flex items-center justify-between"
            data-testid="tr-freight-calculated"
          >
            <div>
              <p className="text-[11px] uppercase tracking-widest text-gado-muted">Valor do frete calculado</p>
              <p className="text-[11px] text-gado-muted">
                {formatNum(computedFreightKm, 1)} km × 2 (ida e volta) × {formatBRL(num(f.transport_km_value) || 0)}
              </p>
            </div>
            <p className="font-cabinet text-lg font-extrabold text-gado-green" data-testid="tr-freight-value">
              {formatBRL(computedFreight)}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransportOpen(false)}>Cancelar</Button>
            <Button onClick={() => setTransportOpen(false)} data-testid="tr-save" className="bg-gado-green hover:bg-gado-greenHover text-white">
              Salvar transporte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-white/70 text-sm">{label}</span>
      <span className="font-semibold font-mono text-sm">{value}</span>
    </div>
  );
}
